import { db } from '../db';
import { getAppVersion } from '../utils/appVersion';
import { getStartupSyncMs } from './startupTiming';

/** A count that either succeeded or could not be read. */
export type Counted = { ok: true; value: number } | { ok: false };

/** Everything the Phone check page shows, gathered in one read-only pass. */
export interface PhoneCheckReport {
  checkedAt: string;
  appVersion: string;
  online: boolean;
  /** null = the phone does not report storage use. */
  storage: { usageBytes: number; quotaBytes: number } | null;
  photos: { count: Counted; totalBytes: Counted };
  queue: { pending: Counted; failed: Counted; quarantined: Counted; synced: Counted };
  phoneDrafts: { open: Counted; finished: Counted };
  deskDrafts: { open: Counted; finished: Counted };
  compactSessions: { total: Counted; finished: Counted };
  startupSyncMs: number | null;
  tables: Array<{ name: string; count: Counted }>;
}

const FAILED: Counted = { ok: false };

const safeCount = async (fn: () => Promise<number>): Promise<Counted> => {
  try {
    return { ok: true, value: await fn() };
  } catch {
    return FAILED;
  }
};

const readStorage = async (): Promise<PhoneCheckReport['storage']> => {
  try {
    const storage = (navigator as Navigator & { storage?: StorageManager }).storage;
    if (!storage || typeof storage.estimate !== 'function') return null;
    const { usage, quota } = await storage.estimate();
    if (typeof usage !== 'number' || typeof quota !== 'number') return null;
    return { usageBytes: usage, quotaBytes: quota };
  } catch {
    return null;
  }
};

/** Walks media one record at a time, reading only each Blob's size. */
const readPhotos = async (): Promise<PhoneCheckReport['photos']> => {
  try {
    let count = 0;
    let bytes = 0;
    await db.media.each(m => {
      count += 1;
      bytes += m.data instanceof Blob ? m.data.size : 0;
    });
    return { count: { ok: true, value: count }, totalBytes: { ok: true, value: bytes } };
  } catch {
    return { count: FAILED, totalBytes: FAILED };
  }
};

/**
 * Reads what the app has stored on this phone. Never writes. Every count runs
 * sequentially so a struggling phone is not hit with parallel IndexedDB work,
 * and a count that fails is reported as unreadable without stopping the rest.
 */
export const collectPhoneCheck = async (): Promise<PhoneCheckReport> => {
  const queueStatus = (s: string) =>
    safeCount(() => db.fieldScoutQueue.where('syncStatus').equals(s).count());

  const queue = {
    pending: await queueStatus('pending'),
    failed: await queueStatus('failed'),
    quarantined: await queueStatus('quarantined'),
    synced: await queueStatus('synced'),
  };
  const phoneDrafts = {
    open: await safeCount(() => db.fieldScoutDrafts.where('isComplete').equals(0).count()),
    finished: await safeCount(() => db.fieldScoutDrafts.where('isComplete').equals(1).count()),
  };
  const deskDrafts = {
    open: await safeCount(() => db.fieldScoutDeskDrafts.where('isComplete').equals(0).count()),
    finished: await safeCount(() => db.fieldScoutDeskDrafts.where('isComplete').equals(1).count()),
  };
  const compactSessions = {
    total: await safeCount(() => db.compactSessions.count()),
    finished: await safeCount(() => db.compactSessions.where('isComplete').equals(1).count()),
  };
  const photos = await readPhotos();
  const storage = await readStorage();

  const tables: PhoneCheckReport['tables'] = [];
  for (const table of [...db.tables].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))) {
    tables.push({ name: table.name, count: await safeCount(() => table.count()) });
  }

  return {
    checkedAt: new Date().toISOString(),
    appVersion: getAppVersion(),
    online: navigator.onLine,
    storage,
    photos,
    queue,
    phoneDrafts,
    deskDrafts,
    compactSessions,
    startupSyncMs: getStartupSyncMs(),
    tables,
  };
};
